/**
 * YOUR REVIEWS TABLE — sorting & filtering (presentation only).
 *
 * This module NEVER computes a financial value. Recovery classification is
 * derived exclusively from the canonical dashboard metrics module
 * (`recoveryAmountFor` / `hasCalculableRecovery` / `isRecoveryEligible`), and
 * canonical status buckets come from the same place. Nothing here mutates the
 * source array.
 */

import type { Case } from "@/lib/cases";
import { normalizeVin, vinMatches } from "@/lib/vin";
import {
  hasCalculableRecovery,
  isCompleted,
  isInProgress,
  isNegotiating,
  isRecoveryEligible,
  isReportReady,
  recoveryAmountFor,
  vendorLabel,
  IN_PROGRESS_STATUSES,
  NEGOTIATION_STATUSES,
  READY_STATUSES,
} from "@/lib/dashboard/metrics";

/* ───────────────────────── Types ───────────────────────── */

export type StatusFilter = "all" | "in_progress" | "ready" | "negotiating" | "completed";
export type RecoveryFilter = "all" | "identified" | "none" | "pending";
export type DateFilter = "all" | "today" | "7d" | "30d" | "90d" | "custom";
export type SortKey = "default" | "file" | "vendor" | "status" | "submitted" | "recovery";
export type SortDirection = "asc" | "desc";

export interface ReviewTableState {
  q: string;
  /** Claim number search (file number or the customer's insurance claim #). */
  claimNumber: string;
  /** Vendor phone search — formatting-insensitive (digits only). */
  vendorPhone: string;
  /** Last 6 of VIN search — case- and formatting-insensitive. */
  vinLast6: string;
  status: StatusFilter;
  recovery: RecoveryFilter;
  date: DateFilter;
  start: string;
  end: string;
  sort: SortKey;
  direction: SortDirection;
}

export const DEFAULT_TABLE_STATE: ReviewTableState = {
  q: "",
  claimNumber: "",
  vendorPhone: "",
  vinLast6: "",
  status: "all",
  recovery: "all",
  date: "all",
  start: "",
  end: "",
  sort: "default",
  direction: "asc",
};

export const STATUS_FILTERS: readonly StatusFilter[] = [
  "all",
  "in_progress",
  "ready",
  "negotiating",
  "completed",
];
export const RECOVERY_FILTERS: readonly RecoveryFilter[] = ["all", "identified", "none", "pending"];
export const DATE_FILTERS: readonly DateFilter[] = ["all", "today", "7d", "30d", "90d", "custom"];
export const SORT_KEYS: readonly SortKey[] = [
  "default",
  "file",
  "vendor",
  "status",
  "submitted",
  "recovery",
];

export const STATUS_FILTER_LABELS: Record<StatusFilter, string> = {
  all: "All Statuses",
  in_progress: "In Progress",
  ready: "Reports Ready",
  negotiating: "Negotiating",
  completed: "Review Complete",
};

export const RECOVERY_FILTER_LABELS: Record<RecoveryFilter, string> = {
  all: "All",
  identified: "Recovery Identified",
  none: "No Supported Recovery",
  pending: "Recovery Calculation Pending",
};

export const DATE_FILTER_LABELS: Record<DateFilter, string> = {
  all: "All Time",
  today: "Today",
  "7d": "Last 7 Days",
  "30d": "Last 30 Days",
  "90d": "Last 90 Days",
  custom: "Custom Range",
};

/* ───────────────────────── Sort presets (dropdown ⇄ headers) ───────────────────────── */

export type SortPreset = { value: string; label: string; sort: SortKey; direction: SortDirection };

/**
 * The Sort dropdown is a discoverable alias for the exact same sort state the
 * column headers set. Both surfaces read and write `{ sort, direction }`.
 */
export const SORT_PRESETS: readonly SortPreset[] = [
  { value: "default", label: "Newest Submitted", sort: "default", direction: "asc" },
  { value: "oldest", label: "Oldest Submitted", sort: "submitted", direction: "asc" },
  { value: "recovery_desc", label: "Highest Recovery", sort: "recovery", direction: "desc" },
  { value: "recovery_asc", label: "Lowest Recovery", sort: "recovery", direction: "asc" },
  { value: "vendor_asc", label: "Vendor A–Z", sort: "vendor", direction: "asc" },
  { value: "vendor_desc", label: "Vendor Z–A", sort: "vendor", direction: "desc" },
  { value: "status", label: "Workflow Status", sort: "status", direction: "asc" },
  { value: "file", label: "File Number", sort: "file", direction: "asc" },
];

/** Map the live sort state onto a dropdown value (falls back to the default). */
export function sortPresetValue(state: { sort: SortKey; direction: SortDirection }): string {
  const match = SORT_PRESETS.find(
    (o) => o.sort === state.sort && o.direction === state.direction,
  );
  return match ? match.value : "default";
}

export function sortPresetState(value: string): { sort: SortKey; direction: SortDirection } {
  const match = SORT_PRESETS.find((o) => o.value === value);
  return match
    ? { sort: match.sort, direction: match.direction }
    : { sort: "default", direction: "asc" };
}

/* ───────────────────────── Active filter chips ───────────────────────── */

export type FilterChipKey =
  | "q"
  | "claimNumber"
  | "vendorPhone"
  | "vinLast6"
  | "status"
  | "recovery"
  | "date";

export interface FilterChip {
  key: FilterChipKey;
  label: string;
  /** Patch that clears only this filter. */
  clear: Partial<ReviewTableState>;
}

/** Compact chips describing every non-default filter (sorting is not a chip). */
export function activeFilterChips(state: ReviewTableState): FilterChip[] {
  const chips: FilterChip[] = [];
  if (state.q.trim())
    chips.push({ key: "q", label: `Search: ${state.q.trim()}`, clear: { q: "" } });
  if (state.claimNumber.trim())
    chips.push({
      key: "claimNumber",
      label: `Claim #: ${state.claimNumber.trim()}`,
      clear: { claimNumber: "" },
    });
  if (state.vendorPhone.trim())
    chips.push({
      key: "vendorPhone",
      label: `Vendor Phone #: ${state.vendorPhone.trim()}`,
      clear: { vendorPhone: "" },
    });
  if (state.vinLast6.trim())
    chips.push({
      key: "vinLast6",
      label: `Last 6 of VIN: ${normalizeVin(state.vinLast6)}`,
      clear: { vinLast6: "" },
    });
  if (state.status !== "all")
    chips.push({
      key: "status",
      label: `Status: ${STATUS_FILTER_LABELS[state.status]}`,
      clear: { status: "all" },
    });
  if (state.recovery !== "all")
    chips.push({
      key: "recovery",
      label: `Recovery: ${RECOVERY_FILTER_LABELS[state.recovery]}`,
      clear: { recovery: "all" },
    });
  if (state.date !== "all")
    chips.push({
      key: "date",
      label: `Date: ${DATE_FILTER_LABELS[state.date]}`,
      clear: { date: "all", start: "", end: "" },
    });
  return chips;
}


export const NO_MATCH_MESSAGE = "No reviews match your current filters.";
export const CLEAR_FILTERS_LABEL = "Clear filters";

/* ───────────────────────── Recovery classification ───────────────────────── */

export type RecoveryClass = "positive" | "zero" | "pending" | "unavailable";

/**
 * Canonical recovery state for one review. NULL is never treated as zero.
 */
export function recoveryClassFor(c: Case): RecoveryClass {
  if (!isRecoveryEligible(c)) return "pending";
  const amount = recoveryAmountFor(c);
  if (amount === null) return "unavailable";
  if (!hasCalculableRecovery(c)) return "pending";
  return amount > 0 ? "positive" : "zero";
}

export function matchesRecoveryFilter(c: Case, f: RecoveryFilter): boolean {
  if (f === "all") return true;
  const cls = recoveryClassFor(c);
  if (f === "identified") return cls === "positive";
  if (f === "none") return cls === "zero";
  return cls === "pending" || cls === "unavailable";
}

/* ───────────────────────── Status ───────────────────────── */

export function matchesStatusFilter(c: Case, f: StatusFilter): boolean {
  switch (f) {
    case "in_progress":
      return IN_PROGRESS_STATUSES.includes(c.status);
    case "ready":
      return READY_STATUSES.includes(c.status);
    case "negotiating":
      return NEGOTIATION_STATUSES.includes(c.status);
    case "completed":
      return c.status === "completed";
    default:
      return true;
  }
}

/** Workflow priority: In Progress → Findings Ready → Negotiating → Completed. */
export function workflowRank(c: Case): number {
  if (isInProgress(c)) return 0;
  if (isReportReady(c)) return 1;
  if (isNegotiating(c)) return 2;
  if (isCompleted(c)) return 3;
  return 4;
}

/* ───────────────────────── Dates ───────────────────────── */

function startOfLocalDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function endOfLocalDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).getTime();
}

function parseLocalDateInput(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function customRangeError(start: string, end: string): string | null {
  const s = parseLocalDateInput(start);
  const e = parseLocalDateInput(end);
  if (start && !s) return "Enter a valid start date.";
  if (end && !e) return "Enter a valid end date.";
  if (s && e && startOfLocalDay(s) > startOfLocalDay(e))
    return "Start date must be on or before the end date.";
  return null;
}

export function matchesDateFilter(
  c: Case,
  f: DateFilter,
  opts: { start?: string; end?: string; now?: Date } = {},
): boolean {
  if (f === "all") return true;
  const created = new Date(c.createdAt).getTime();
  if (Number.isNaN(created)) return false;
  const now = opts.now ?? new Date();

  if (f === "custom") {
    if (customRangeError(opts.start ?? "", opts.end ?? "")) return true;
    const s = parseLocalDateInput(opts.start ?? "");
    const e = parseLocalDateInput(opts.end ?? "");
    if (s && created < startOfLocalDay(s)) return false;
    if (e && created > endOfLocalDay(e)) return false;
    return true;
  }

  const days = f === "today" ? 1 : f === "7d" ? 7 : f === "30d" ? 30 : 90;
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1));
  return created >= startOfLocalDay(from) && created <= endOfLocalDay(now);
}

/* ───────────────────────── Search ───────────────────────── */

export function matchesQuery(c: Case, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const vendor = vendorLabel(c).text.toLowerCase();
  return c.id.toLowerCase().includes(needle) || vendor.includes(needle);
}

/** Alphanumeric-only comparison key (ignores dashes, spaces, punctuation). */
function alnum(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** Digits-only comparison key for phone numbers. */
function digits(value: string): string {
  return value.replace(/\D/g, "");
}

/** Matches the claim file number OR the customer-provided insurance claim #. */
export function matchesClaimNumber(c: Case, q: string): boolean {
  const needle = alnum(q);
  if (!needle) return true;
  const candidates = [c.id, c.details.claimNumber ?? ""];
  return candidates.some((v) => v && alnum(v).includes(needle));
}

/** Matches the vendor phone number, ignoring formatting entirely. */
export function matchesVendorPhone(c: Case, q: string): boolean {
  const needle = digits(q);
  if (!needle) return true;
  const phone = digits(c.details.vendorPhone ?? "");
  return phone.length > 0 && phone.includes(needle);
}

/** Matches the last 6 (or any partial) of the stored VIN. */
export function matchesVinLast6(c: Case, q: string): boolean {
  if (!normalizeVin(q)) return true;
  return vinMatches(c.details.vehicleVin, q);
}

/* ───────────────────────── Sorting ───────────────────────── */

function submittedTime(c: Case): number {
  const t = new Date(c.createdAt).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** Canonical file sequence: INV-YYYY-MMDD-NNNN → sortable number. */
export function fileSequence(c: Case): number | null {
  const m = /(\d{4})-(\d{2})(\d{2})-(\d{4})/.exec(c.id);
  if (!m) return null;
  return Number(`${m[1]}${m[2]}${m[3]}${m[4]}`);
}

function compareFile(a: Case, b: Case): number {
  const sa = fileSequence(a);
  const sb = fileSequence(b);
  if (sa !== null && sb !== null && sa !== sb) return sa - sb;
  if (sa !== null && sb === null) return -1;
  if (sa === null && sb !== null) return 1;
  const ta = submittedTime(a);
  const tb = submittedTime(b);
  if (ta !== tb) return ta - tb;
  return a.id.localeCompare(b.id, undefined, { sensitivity: "base" });
}

function compareVendor(a: Case, b: Case): number {
  return vendorLabel(a).text.localeCompare(vendorLabel(b).text, undefined, {
    sensitivity: "base",
  });
}

/** Ordering rank within an ascending recovery sort. */
function recoveryRankAsc(cls: RecoveryClass): number {
  return cls === "zero" ? 0 : cls === "positive" ? 1 : cls === "pending" ? 2 : 3;
}

/** Ordering rank within a descending recovery sort. */
function recoveryRankDesc(cls: RecoveryClass): number {
  return cls === "positive" ? 0 : cls === "zero" ? 1 : cls === "pending" ? 2 : 3;
}

function compareRecovery(a: Case, b: Case, direction: SortDirection): number {
  const ca = recoveryClassFor(a);
  const cb = recoveryClassFor(b);
  const ra = direction === "asc" ? recoveryRankAsc(ca) : recoveryRankDesc(ca);
  const rb = direction === "asc" ? recoveryRankAsc(cb) : recoveryRankDesc(cb);
  if (ra !== rb) return ra - rb;
  if (ca === "positive" && cb === "positive") {
    const va = recoveryAmountFor(a) ?? 0;
    const vb = recoveryAmountFor(b) ?? 0;
    if (va !== vb) return direction === "asc" ? va - vb : vb - va;
  }
  return submittedTime(b) - submittedTime(a);
}

/** Default order: most recently submitted first. */
export function compareDefault(a: Case, b: Case): number {
  return submittedTime(b) - submittedTime(a);
}

export function sortCases(cases: Case[], sort: SortKey, direction: SortDirection): Case[] {
  const list = cases.slice();
  if (sort === "default") return list.sort(compareDefault);
  const flip = direction === "desc" ? -1 : 1;
  return list.sort((a, b) => {
    switch (sort) {
      case "file":
        return compareFile(a, b) * flip;
      case "vendor":
        return compareVendor(a, b) * flip;
      case "status": {
        const d = workflowRank(a) - workflowRank(b);
        return d !== 0 ? d * flip : compareDefault(a, b);
      }
      case "submitted": {
        const d = submittedTime(a) - submittedTime(b);
        return d !== 0 ? d * flip : a.id.localeCompare(b.id);
      }
      case "recovery":
        return compareRecovery(a, b, direction);
      default:
        return compareDefault(a, b);
    }
  });
}

/* ───────────────────────── Composition ───────────────────────── */

/**
 * Apply search → filters → sorting, in that fixed order, to the already
 * ownership-scoped dashboard projection.
 */
export function applyReviewTableState(
  cases: Case[],
  state: ReviewTableState,
  now?: Date,
): Case[] {
  const filtered = cases.filter(
    (c) =>
      matchesQuery(c, state.q) &&
      matchesClaimNumber(c, state.claimNumber) &&
      matchesVendorPhone(c, state.vendorPhone) &&
      matchesVinLast6(c, state.vinLast6) &&
      matchesStatusFilter(c, state.status) &&
      matchesRecoveryFilter(c, state.recovery) &&
      matchesDateFilter(c, state.date, { start: state.start, end: state.end, now }),
  );
  return sortCases(filtered, state.sort, state.direction);
}

export function isDefaultTableState(state: ReviewTableState): boolean {
  return (
    state.q.trim() === "" &&
    state.claimNumber.trim() === "" &&
    state.vendorPhone.trim() === "" &&
    state.vinLast6.trim() === "" &&
    state.status === "all" &&
    state.recovery === "all" &&
    state.date === "all" &&
    state.sort === "default"
  );
}

export function resultCountLabel(shown: number, total: number, narrowed = true): string {
  const noun = `review${(narrowed ? total : shown) === 1 ? "" : "s"}`;
  return narrowed ? `Showing ${shown} of ${total} ${noun}` : `Showing ${shown} ${noun}`;
}

/** Sort cycle: ascending → descending → default. */
export function nextSortState(
  current: { sort: SortKey; direction: SortDirection },
  column: Exclude<SortKey, "default">,
): { sort: SortKey; direction: SortDirection } {
  if (current.sort !== column) return { sort: column, direction: "asc" };
  if (current.direction === "asc") return { sort: column, direction: "desc" };
  return { sort: "default", direction: "asc" };
}

export function ariaSortFor(
  state: { sort: SortKey; direction: SortDirection },
  column: Exclude<SortKey, "default">,
): "none" | "ascending" | "descending" {
  if (state.sort !== column) return "none";
  return state.direction === "asc" ? "ascending" : "descending";
}

/* ───────────────────────── URL validation ───────────────────────── */

function pick<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === "string" && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

function safeDate(value: unknown): string {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : "";
}

/** Validate untrusted URL search params into a safe table state. */
export function parseReviewTableSearch(raw: Record<string, unknown>): ReviewTableState {
  return {
    q: typeof raw.q === "string" ? raw.q.slice(0, 120) : "",
    claimNumber: typeof raw.claimNumber === "string" ? raw.claimNumber.slice(0, 60) : "",
    vendorPhone: typeof raw.vendorPhone === "string" ? raw.vendorPhone.slice(0, 30) : "",
    vinLast6: typeof raw.vinLast6 === "string" ? raw.vinLast6.slice(0, 17) : "",
    status: pick(raw.status, STATUS_FILTERS, "all"),
    recovery: pick(raw.recovery, RECOVERY_FILTERS, "all"),
    date: pick(raw.date, DATE_FILTERS, "all"),
    start: safeDate(raw.start),
    end: safeDate(raw.end),
    sort: pick(raw.sort, SORT_KEYS, "default"),
    direction: pick(raw.direction, ["asc", "desc"] as const, "asc"),
  };
}

/** Strip defaults so the URL stays clean. */
export function serializeReviewTableState(state: ReviewTableState): Record<string, string> {
  const out: Record<string, string> = {};
  if (state.q.trim()) out.q = state.q;
  if (state.claimNumber.trim()) out.claimNumber = state.claimNumber;
  if (state.vendorPhone.trim()) out.vendorPhone = state.vendorPhone;
  if (state.vinLast6.trim()) out.vinLast6 = state.vinLast6;
  if (state.status !== "all") out.status = state.status;
  if (state.recovery !== "all") out.recovery = state.recovery;
  if (state.date !== "all") out.date = state.date;
  if (state.date === "custom" && state.start) out.start = state.start;
  if (state.date === "custom" && state.end) out.end = state.end;
  if (state.sort !== "default") {
    out.sort = state.sort;
    out.direction = state.direction;
  }
  return out;
}
